import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
  DataService,
  WorkflowContextService,
  Utilities
} from 'src/app/services';
import { MigrationProcessing, Client, User, Document } from 'src/app/models';
import {
  UploadComponent,
  UploadResponse
} from 'src/app/components/upload/upload.component';

@Component({
  selector: 'wm-migration-processing-create',
  templateUrl: './migration-processing-create.component.html',
  styleUrls: ['./migration-processing-create.component.css'],
  standalone: false
})
export class MigrationProcessingCreateComponent implements OnInit {
  @ViewChild('metadataUploadComponent', { static: false })
  metadataUploadComponent: UploadComponent;
  @ViewChild('applicationDataUploadComponent', { static: false })
  applicationDataUploadComponent: UploadComponent;

  migrationProcessing: MigrationProcessing = new MigrationProcessing();
  jurisdictions: Client[] = [];

  uploadKey: string = '';
  metadataUploaded: boolean = false;
  applicationDataUploaded: boolean = false;

  loading: boolean = false;
  error: string = null;
  success: boolean = false;

  metadataValidationStatus: {
    isValidating: boolean;
    isValid: boolean;
    errors: string[];
    warnings: string[];
    supportedTypes: string[];
    unsupportedTypes: string[];
  } | null = null;
  showMetadataValidationDetails: boolean = false;

  clientId: string = null;
  hasClientContext: boolean = false;

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _dataSvc: DataService,
    private _context: WorkflowContextService
  ) { }

  ngOnInit() {
    this.uploadKey = Utilities.generateId();

    // Get client ID from route parameters
    this._route.parent?.parent?.paramMap.subscribe(params => {
      this.clientId = params.get('clientId');
      this.hasClientContext = !!this.clientId;

      if (this.hasClientContext) {
        this.migrationProcessing.clientId = this.clientId;
        this.loadJurisdictionInfo();
      } else {
        this.loadJurisdictions();
      }
    });
  }

  loadJurisdictions() {
    this.loading = true;
    this._dataSvc.getClients().subscribe({
      next: clients => {
        this.jurisdictions = clients;
        this.loading = false;
      },
      error: err => {
        this.error = 'Failed to load jurisdictions: ' + err.message;
        this.loading = false;
      }
    });
  }

  loadJurisdictionInfo() {
    if (this.clientId) {
      this._dataSvc.getClient(this.clientId).subscribe({
        next: client => {
          this.migrationProcessing.jurisdiction = client.name;
        },
        error: err => {
          this.error = 'Failed to load jurisdiction info: ' + err.message;
        }
      });
    }
  }

  onJurisdictionSelected(jurisdictionId: string) {
    this.migrationProcessing.clientId = jurisdictionId;
    const jurisdiction = this.jurisdictions.find(j => j.id === jurisdictionId);
    if (jurisdiction) {
      this.migrationProcessing.jurisdiction = jurisdiction.name;
    }
  }

  onMetadataFileUploaded(response: UploadResponse) {
    this.migrationProcessing.metaDataFilePath = response.path;
    this.metadataUploaded = true;

    // Note: Full validation (including field references) will run when you click "Create Workflow Migration"
    // This initial validation only checks basic JSON structure and field types
    this.validateMetadataFile(response.path);
  }

  onMetadataFileDeleted(document: Document) {
    this.migrationProcessing.metaDataFilePath = null;
    this.metadataUploaded = false;
    this.metadataValidationStatus = null;
    this.showMetadataValidationDetails = false;
  }

  onApplicationDataFileUploaded(response: UploadResponse) {
    this.migrationProcessing.applicationDataFilePath = response.path;
    this.applicationDataUploaded = true;
  }

  onApplicationDataFileDeleted(document: Document) {
    this.migrationProcessing.applicationDataFilePath = null;
    this.applicationDataUploaded = false;
  }

  saveMigrationProcessing() {
    // Validate required fields
    if (!this.migrationProcessing.clientId) {
      this.error = 'Please select a jurisdiction';
      return;
    }

    if (
      !this.migrationProcessing.sourceSystem ||
      this.migrationProcessing.sourceSystem.trim() === ''
    ) {
      this.error = 'Please enter a source system';
      return;
    }

    if (!this.metadataUploaded) {
      this.error = 'Please upload a metadata file';
      return;
    }

    // Check initial metadata validation status (basic structure and field types)
    // Note: Comprehensive validation (field references, activity order) happens during creation
    if (
      this.metadataValidationStatus &&
      this.metadataValidationStatus.errors.length > 0
    ) {
      this.error = 'Please fix metadata validation errors before saving';
      return;
    }

    // Application data file is optional

    this.loading = true;
    this.error = null;

    // Save the migration processing record
    this._dataSvc.saveMigrationProcessing(this.migrationProcessing).subscribe({
      next: savedProcessing => {
        // Parse metadata to create data entities and routes (which includes validation)
        this._dataSvc.parseMetadataFile(savedProcessing.id).subscribe({
          next: result => {
            // Update validation status based on parse results
            this.metadataValidationStatus = {
              isValidating: false,
              isValid:
                !result.validationErrors ||
                result.validationErrors.length === 0,
              errors: result.validationErrors || [],
              warnings: result.validationWarnings || [],
              supportedTypes: [],
              unsupportedTypes: []
            };

            // Check for validation errors
            if (result.validationErrors && result.validationErrors.length > 0) {
              this.error = `Metadata validation failed with ${result.validationErrors.length} error(s). Please review the errors below.`;
              this.loading = false;
              this.showMetadataValidationDetails = true; // Auto-expand details
              return;
            }

            if (
              result.validationWarnings &&
              result.validationWarnings.length > 0
            ) {
              console.warn(
                'Metadata validation warnings:',
                result.validationWarnings
              );
            }

            this.success = true;
            this.loading = false;

            // Navigate back to the workflow migration list
            setTimeout(() => {
              if (this.hasClientContext) {
                if (this._context.useNewLayout) {
                  this._router.navigate(['../../settings'], { relativeTo: this._route });
                } else {
                  this._router.navigate(['../../workflow-migration'], { relativeTo: this._route });
                }
              } else {
                this._router.navigate(['/settings/workflow-migration']);
              }
            }, 1500);
          },
          error: parseErr => {
            // Even if parsing fails, we still created the record, so navigate
            console.error('Failed to parse metadata:', parseErr);
            this.success = true;
            this.loading = false;

            setTimeout(() => {
              if (this.hasClientContext) {
                if (this._context.useNewLayout) {
                  this._router.navigate(['../../settings'], { relativeTo: this._route });
                } else {
                  this._router.navigate(['../../workflow-migration'], { relativeTo: this._route });
                }
              } else {
                this._router.navigate(['/settings/workflow-migration']);
              }
            }, 1500);
          }
        });
      },
      error: err => {
        this.error = 'Failed to save migration processing: ' + err.message;
        this.loading = false;
      }
    });
  }

  validateMetadataFile(filePath: string) {
    this.metadataValidationStatus = {
      isValidating: true,
      isValid: false,
      errors: [],
      warnings: [],
      supportedTypes: [],
      unsupportedTypes: []
    };

    this._dataSvc.validateMigrationMetadata(filePath).subscribe({
      next: result => {
        this.metadataValidationStatus = {
          isValidating: false,
          isValid: result.isValid,
          errors: result.errors || [],
          warnings: result.warnings || [],
          supportedTypes: result.supportedTypes || [],
          unsupportedTypes: result.unsupportedTypes || []
        };
      },
      error: err => {
        this.metadataValidationStatus = {
          isValidating: false,
          isValid: false,
          errors: [
            'Failed to validate metadata: ' +
              (err.error?.message || err.message)
          ],
          warnings: [],
          supportedTypes: [],
          unsupportedTypes: []
        };
      }
    });
  }

  cancel() {
    if (this.hasClientContext) {
      if (this._context.useNewLayout) {
        this._router.navigate(['../../settings'], { relativeTo: this._route });
      } else {
        this._router.navigate(['../../workflow-migration'], { relativeTo: this._route });
      }
    } else {
      this._router.navigate(['/settings/workflow-migration']);
    }
  }
}
